/**
 * Parse single pins, bulk text, and CSV into { lat, lon, note } rows.
 */

/**
 * @param {string|number} lat
 * @param {string|number} lon
 */
export function parseCoordPair(lat, lon) {
    const la = Number(String(lat).trim());
    const lo = Number(String(lon).trim());
    if (!Number.isFinite(la) || !Number.isFinite(lo)) {
        return null;
    }
    if (la < -90 || la > 90 || lo < -180 || lo > 180) {
        return null;
    }
    return { lat: la, lon: lo };
}

/**
 * @param {string} line
 */
export function splitCsvLine(line) {
    const parts = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }
        if (ch === ',' && !inQuotes) {
            parts.push(current.trim());
            current = '';
            continue;
        }
        current += ch;
    }

    parts.push(current.trim());
    return parts.map((part) => part.replace(/^"|"$/g, '').trim());
}

/**
 * @param {string} line
 * @param {number} lineNum
 */
export function parsePinInputLine(line, lineNum) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) {
        return { skip: true };
    }

    if (/^lat(itude)?\b/i.test(trimmed) && /\blon(g(itude)?)?\b/i.test(trimmed)) {
        return { skip: true };
    }

    let lat;
    let lon;
    let note = '';

    if (trimmed.includes(',')) {
        const parts = splitCsvLine(trimmed);
        if (parts.length < 2) {
            return { error: `Line ${lineNum}: need latitude and longitude` };
        }
        lat = parts[0];
        lon = parts[1];
        note = parts.slice(2).join(', ').trim();
    } else {
        const parts = trimmed.split(/\s+/);
        if (parts.length < 2) {
            return { error: `Line ${lineNum}: need latitude and longitude` };
        }
        lat = parts[0];
        lon = parts[1];
        note = parts.slice(2).join(' ').trim();
    }

    const coords = parseCoordPair(lat, lon);
    if (!coords) {
        return { error: `Line ${lineNum}: invalid coordinates (${lat}, ${lon})` };
    }

    return { pin: { ...coords, note } };
}

/**
 * @param {string} text
 */
export function parseBulkPinText(text) {
    const lines = String(text || '').split(/\r?\n/);
    /** @type {{ lat: number, lon: number, note: string }[]} */
    const pins = [];
    /** @type {string[]} */
    const errors = [];

    for (let i = 0; i < lines.length; i++) {
        const result = parsePinInputLine(lines[i], i + 1);
        if (result.skip) {
            continue;
        }
        if (result.error) {
            errors.push(result.error);
            continue;
        }
        if (result.pin) {
            pins.push(result.pin);
        }
    }

    return { pins, errors };
}

/**
 * @param {string} text
 */
export function parsePinCsvText(text) {
    return parseBulkPinText(text);
}
